/**
 * 音频记录（Alice 页面 audios 的复刻）：语音合成与语音转写的请求卡片墙与结算结果。
 *
 * 背景：数据来自控制面 /control/admin/audio-logs，即每次请求结算后的用量记录。Alice 的音乐生成、音频工具、
 *   网盘占用、存储与归档状态、审核在本仓没有对应物：语音合成与转写是同步请求，不建任务、不入网盘、不存音频，
 *   所以卡片上没有播放器与审核按钮，筛选里没有存储、审核、归档三项，第三张指标卡换成成功率。
 * 设计意图：卡片 DOM、类名与字段顺序对齐 Alice；没有进行中的任务，因此不轮询。
 * 关键约束：合成文本只在请求正文仍在保存期内才有；转写不保存任何文本。
 */
import { useEffect, useRef, useState } from 'react'
import type { AudioLogs, AudioRecord } from '@ap/api-client/admin-runtime2'
import { runtime2Api } from '@ap/api-client/admin-runtime2'
import { AG_runtime2 as G, errorText2 } from '@ap/i18n/admin-runtime2'
import { useI18n } from '@ap/i18n'
import { fmtDateTime } from '@ap/ui'
import { OpsNote, OpsPage, Stat, Stats, Toolbar } from '../../alice/kit.tsx'
import { PILL, PageNav, Pill, Section, ToolInput, ToolSelect, ellipsis, type PillTone } from '../../alice/kit-runtime2.tsx'
import { useUsd } from '../../shared.tsx'

const PAGE = 18

interface Filters {
  kind: string
  start: string
  end: string
  user: string
  model: string
  keyword: string
  status: string
}

const EMPTY: Filters = { kind: 'all', start: '', end: '', user: '', model: '', keyword: '', status: 'all' }

const KIND_TONE: Record<string, PillTone> = { speech: PILL.sky, transcription: PILL.purple }
const STATUS_TONE: Record<string, PillTone> = { succeeded: PILL.green, failed: PILL.red }

export function Audios() {
  const { t, locale } = useI18n()
  const usd = useUsd()
  const [draft, setDraft] = useState<Filters>(EMPTY)
  const [applied, setApplied] = useState<Filters>(EMPTY)
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState<AudioLogs | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const seq = useRef(0)

  useEffect(() => {
    const my = ++seq.current
    setLoading(true)
    runtime2Api
      .audioLogs({
        kind: applied.kind,
        user: applied.user.trim(),
        model: applied.model.trim(),
        keyword: applied.keyword.trim(),
        status: applied.status,
        start_date: applied.start,
        end_date: applied.end ? `${applied.end} 23:59:59` : undefined,
        limit: PAGE,
        offset,
      })
      .then((d) => {
        if (my !== seq.current) return
        setData(d)
        setError(null)
      })
      .catch((e) => {
        if (my !== seq.current) return
        console.error('[audios] 加载失败', e)
        setError(e)
      })
      .finally(() => {
        if (my === seq.current) setLoading(false)
      })
  }, [applied, offset])

  const apply = (next: Filters) => {
    setDraft(next)
    setApplied(next)
    setOffset(0)
  }
  const setField = (patch: Partial<Filters>, immediate = false) => {
    const next = { ...draft, ...patch }
    if (immediate) apply(next)
    else setDraft(next)
  }

  const s = data?.summary
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const page = Math.floor(offset / PAGE) + 1
  const rate = s && total > 0 ? `${((s.succeeded * 100) / total).toFixed(1)}%` : '—'

  return (
    <OpsPage eyebrow={t(G.audios.eyebrow)} title={t(G.audios.title)} desc={t(G.audios.desc)} chip={t(G.audios.chip)}>
      <Stats>
        <Stat
          label={t(G.audios.statTotal)}
          value={s ? total.toLocaleString() : '—'}
          sub={s ? t(G.audios.statTotalSub, { ok: s.succeeded, failed: s.failed }) : undefined}
        />
        <Stat
          label={t(G.audios.statKinds)}
          value={s ? `${s.speech} / ${s.transcription}` : '—'}
          sub={s ? t(G.audios.statKindsSub, { c: s.speech_characters.toLocaleString(), s: s.transcription_seconds.toLocaleString() }) : undefined}
        />
        <Stat label={t(G.audios.statRate)} value={rate} sub={t(G.audios.statRateSub)} />
        <Stat
          label={t(G.audios.statCharge)}
          value={s ? `${usd(s.user_cost_units, 4)} / ${usd(s.margin_units, 4)}` : '—'}
          sub={s ? t(G.audios.statChargeSub, { m: usd(s.margin_units, 4), n: s.known_cost_tasks }) : undefined}
        />
      </Stats>

      <Toolbar count={s ? t(G.audios.totalBadge, { total: total.toLocaleString() }) : undefined}>
        <ToolSelect
          value={draft.kind}
          onChange={(e) => setField({ kind: e.target.value }, true)}
          options={[{ value: 'all', label: t(G.audios.kindAll) }, ...Object.entries(G.audios.kind).map(([value, entry]) => ({ value, label: t(entry) }))]}
        />
        <ToolInput type="date" aria-label={t(G.audios.start)} value={draft.start} onChange={(e) => setField({ start: e.target.value }, true)} />
        <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t(G.common.to)}</span>
        <ToolInput type="date" aria-label={t(G.audios.end)} value={draft.end} onChange={(e) => setField({ end: e.target.value }, true)} />
        <ToolInput width={190} placeholder={t(G.audios.userPh)} value={draft.user} onChange={(e) => setField({ user: e.target.value })} onEnter={() => apply(draft)} />
        <ToolInput width={140} placeholder={t(G.audios.modelPh)} value={draft.model} onChange={(e) => setField({ model: e.target.value })} onEnter={() => apply(draft)} />
        <ToolInput width={190} placeholder={t(G.audios.keywordPh)} value={draft.keyword} onChange={(e) => setField({ keyword: e.target.value })} onEnter={() => apply(draft)} />
        <ToolSelect
          value={draft.status}
          onChange={(e) => setField({ status: e.target.value }, true)}
          options={[{ value: 'all', label: t(G.audios.statusAll) }, ...Object.entries(G.audios.status).map(([value, entry]) => ({ value, label: t(entry) }))]}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => apply(draft)}>
          {t(G.common.filter)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => apply(EMPTY)}>
          {t(G.common.clear)}
        </button>
      </Toolbar>
      <OpsNote>{t(G.audios.note)}</OpsNote>

      <Section title={t(G.audios.sectionTitle)} copy={t(G.audios.sectionCopy)}>
        <div id="audiosGrid">
          {loading && !data ? (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40 }}>
              {t(G.common.loading)}
            </div>
          ) : error ? (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40, color: 'var(--error-text)' }}>
              {t(G.common.loadFailed, { msg: errorText2(error, locale) })}
            </div>
          ) : data && data.records.length ? (
            data.records.map((r) => <AudioCard key={r.request_id} r={r} />)
          ) : (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40 }}>
              {t(G.audios.empty)}
            </div>
          )}
        </div>
        <div style={{ padding: '4px 16px 16px', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
          {total > 0 && (
            <PageNav
              page={page}
              pages={pages}
              label={t(G.common.pageOf, { p: page, n: pages })}
              prev={t(G.common.prev)}
              next={t(G.common.next)}
              onPage={(p) => setOffset((p - 1) * PAGE)}
            />
          )}
        </div>
      </Section>
    </OpsPage>
  )
}

function AudioCard({ r }: { r: AudioRecord }) {
  const { t, locale } = useI18n()
  const usd = useUsd()
  const speech = r.kind === 'speech'
  const user = r.account_name || t(G.common.accountFallback, { id: r.account_id.slice(0, 8) })
  const title = t(speech ? G.audios.titleSpeech : G.audios.titleAsr)
  const detail = speech ? t(G.common.characters, { n: r.characters.toLocaleString() }) : t(G.audios.detailAsr, { s: (r.duration_ms / 1000).toLocaleString() })
  const text = speech ? r.text || t(G.audios.noText) : ''
  const charge = r.user_cost_units == null ? t(G.common.unsettled) : usd(r.user_cost_units, 4)
  const upstream = r.upstream_cost_units == null ? t(G.common.costUnknown) : t(G.common.cost, { v: usd(r.upstream_cost_units, 4) })
  return (
    <article className="audio-record-card">
      <div className="audio-media">
        <div className="audio-placeholder">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
          <span>{t(speech ? G.audios.placeholderSpeech : G.audios.placeholderAsr)}</span>
        </div>
      </div>
      <div style={{ padding: '12px 13px 13px' }}>
        <div className="video-meta-row">
          <span style={{ display: 'flex', gap: 6 }}>
            <Pill tone={KIND_TONE[r.kind] ?? PILL.gray}>{t(G.audios.kind[r.kind])}</Pill>
            <Pill tone={STATUS_TONE[r.status] ?? PILL.gray}>{t(G.audios.status[r.status])}</Pill>
          </span>
          <span>{fmtDateTime(r.created_at, locale)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
          <span className="mono" title={`account: ${r.account_id}`} style={{ fontSize: 11, fontWeight: 650 }}>
            {user}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{t(G.audios.attempts, { n: r.attempts })}</span>
        </div>
        <div className="audio-title" title={title}>
          {title}
        </div>
        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-tertiary)', ...ellipsis }} title={detail}>
          {detail}
        </div>
        {text && (
          <div className="audio-text" title={text}>
            {text}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10, fontSize: 10 }}>
          <span className="mono" style={{ color: 'var(--primary)', ...ellipsis }} title={r.model_id}>
            {r.model_id}
          </span>
          <span className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>
            {r.upstream_model || r.provider_id}
          </span>
        </div>
        <div className="video-meta-row" style={{ marginTop: 8 }}>
          <span>
            {charge} · {upstream}
          </span>
        </div>
        {r.error_code && (
          <div
            title={r.error_code}
            style={{ marginTop: 8, padding: '7px 8px', borderRadius: 6, background: 'var(--error-bg)', color: 'var(--error-text)', fontSize: 10, lineHeight: 1.45, ...ellipsis }}
          >
            {r.error_code}
          </div>
        )}
        <div className="mono" title={r.request_id} style={{ marginTop: 8, color: 'var(--text-tertiary)', fontSize: 9, ...ellipsis }}>
          {r.request_id}
        </div>
      </div>
    </article>
  )
}
